import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import {
    ArrowLeft,
    Users,
    Download,
    CheckCircle,
    AlertCircle,
    Search,
    FileText,
    Mail,
    ChevronDown,
    ChevronUp,
    User,
    Heart
} from 'lucide-react';
import UnifiedLoader from '../components/UnifiedLoader';

const EventRegistrationsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, API_URL } = useContext(AuthContext);

    const [event, setEvent] = useState(null);
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedRows, setExpandedRows] = useState({});

    useEffect(() => {
        const fetchEventAndRegistrations = async () => {
            try {
                const config = {
                    headers: { Authorization: `Bearer ${user.token}` },
                };
                const [eventRes, regRes] = await Promise.all([
                    axios.get(`${API_URL}/events/${id}`, config),
                    axios.get(`${API_URL}/events/${id}/registrations`, config)
                ]);
                setEvent(eventRes.data);
                setRegistrations(regRes.data);
                setLoading(false);
            } catch (err) {
                console.error('Fehler beim Laden:', err);
                setError('Daten konnten nicht geladen werden.');
                setLoading(false);
            }
        };

        if (user) fetchEventAndRegistrations();
    }, [id, user, API_URL]);

    const handleApproveRegistration = async (regId) => {
        try {
            const config = {
                headers: { Authorization: `Bearer ${user.token}` },
            };
            await axios.put(`${API_URL}/events/registrations/${regId}/approve`, {}, config);
            setRegistrations(registrations.map(r => r._id === regId ? { ...r, status: 'confirmed' } : r));
        } catch (err) {
            console.error('Fehler beim Freigeben:', err);
        }
    };

    const toggleRow = (id) => {
        setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
    };

    // Helper: get the participant name(s) from a registration
    const getParticipantNames = (reg) => {
        const ad = reg.additionalData;
        if (ad?.registrationType === 'children' && ad?.children && Array.isArray(ad.children)) {
            return ad.children.filter(c => c.name?.trim()).map(c => c.name.trim());
        }
        // "self" registration — participant IS the registrant
        return [reg.user ? (reg.user.userData?.scoutName || reg.user.userData?.firstName || reg.user.email) : reg.guestName];
    };

    // Helper: get registrant name
    const getRegistrantName = (reg) => {
        return reg.user ? (reg.user.userData?.scoutName || reg.user.userData?.firstName || reg.user.email) : reg.guestName;
    };

    // Count total actual participants
    const totalParticipants = registrations.reduce((sum, reg) => {
        return sum + getParticipantNames(reg).length;
    }, 0);

    const filteredRegistrations = registrations.filter(reg => {
        const registrant = getRegistrantName(reg);
        const participants = getParticipantNames(reg).join(' ');
        const email = reg.user ? reg.user.email : reg.guestEmail;
        const search = searchTerm.toLowerCase();
        return registrant.toLowerCase().includes(search) ||
               participants.toLowerCase().includes(search) ||
               email.toLowerCase().includes(search);
    });

    if (loading) return <UnifiedLoader fullscreen />;

    if (error) {
        return (
            <div className="p-8 text-center">
                <div className="w-16 h-16 bg-error/10 text-error rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle size={32} />
                </div>
                <h2 className="text-display-small font-bold mb-2">Fehler</h2>
                <p className="text-body-large text-surface-onVariant mb-6">{error}</p>
                <button onClick={() => navigate('/events')} className="md3-btn-filled">Zurück zu Events</button>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full animate-fade-in">
            <header className="sticky top-0 z-30 bg-surface/80 backdrop-blur-md border-b border-outlineVariant/50 px-4 py-4 md:px-8">
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 overflow-hidden">
                        <button
                            onClick={() => navigate(-1)}
                            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-surface-on/5 text-surface-on transition-colors shrink-0"
                        >
                            <ArrowLeft size={24} />
                        </button>
                        <div className="overflow-hidden">
                            <h1 className="text-title-large font-bold text-surface-on truncate">
                                Teilnehmer: {event?.title}
                            </h1>
                            <p className="text-label-small text-surface-onVariant uppercase tracking-widest font-bold opacity-60">
                                {new Date(event?.date).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
                            </p>
                        </div>
                    </div>
                </div>
            </header>

            <main className="flex-1 p-4 md:p-8 w-full max-w-6xl mx-auto">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                    <div className="p-6 bg-primary-container text-primary-onContainer rounded-3xl border border-primary/10 shadow-sm">
                        <div className="text-label-medium uppercase font-bold opacity-70 mb-1">Teilnehmer</div>
                        <div className="text-display-small font-black">{totalParticipants}</div>
                    </div>
                    <div className="p-6 bg-secondary-container text-secondary-onContainer rounded-3xl border border-secondary/10 shadow-sm">
                        <div className="text-label-medium uppercase font-bold opacity-70 mb-1">Anmeldungen</div>
                        <div className="text-display-small font-black">{registrations.length}</div>
                    </div>
                    <div className="p-6 bg-tertiary-container text-tertiary-onContainer rounded-3xl border border-tertiary/10 shadow-sm">
                        <div className="text-label-medium uppercase font-bold opacity-70 mb-1">Ausstehend</div>
                        <div className="text-display-small font-black">
                            {registrations.filter(r => r.status === 'pending').length}
                        </div>
                    </div>
                </div>

                <div className="md3-card-elevated rounded-3xl overflow-hidden border border-outlineVariant/50 bg-surface-containerLowest">
                    {/* Search */}
                    <div className="p-4 border-b border-outlineVariant/50">
                        <div className="relative w-full">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-surface-onVariant opacity-50" size={18} />
                            <input
                                type="text"
                                placeholder="Nach Teilnehmern oder Anmeldern suchen..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-12 pr-4 py-3 rounded-2xl bg-surface-containerHigh border-none focus:ring-2 focus:ring-primary/20 text-surface-on"
                            />
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-surface-containerLow">
                                    <th className="px-6 py-4 text-label-small font-bold text-surface-onVariant uppercase tracking-widest">Teilnehmer</th>
                                    <th className="px-6 py-4 text-label-small font-bold text-surface-onVariant uppercase tracking-widest">Angemeldet von</th>
                                    <th className="px-6 py-4 text-label-small font-bold text-surface-onVariant uppercase tracking-widest">E-Mail</th>
                                    <th className="px-6 py-4 text-label-small font-bold text-surface-onVariant uppercase tracking-widest">Status</th>
                                    <th className="px-6 py-4 text-right text-label-small font-bold text-surface-onVariant uppercase tracking-widest">Aktionen</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-outlineVariant/30">
                                {filteredRegistrations.map((reg) => {
                                    const ad = reg.additionalData;
                                    const isChildReg = ad?.registrationType === 'children' && ad?.children?.length > 0;
                                    const participants = getParticipantNames(reg);
                                    const registrant = getRegistrantName(reg);
                                    const email = reg.user ? reg.user.email : reg.guestEmail;
                                    const isExpanded = expandedRows[reg._id];

                                    return (
                                        <React.Fragment key={reg._id}>
                                            <tr
                                                className={`hover:bg-surface-on/[0.02] transition-colors ${isChildReg ? 'cursor-pointer' : ''}`}
                                                onClick={() => isChildReg && toggleRow(reg._id)}
                                            >
                                                <td className="px-6 py-5">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-black ${isChildReg ? 'bg-secondary-container text-secondary' : 'bg-primary-container text-primary'}`}>
                                                            {isChildReg ? <Users size={16} /> : <User size={16} />}
                                                        </div>
                                                        <div>
                                                            <span className="font-bold text-surface-on block">
                                                                {participants.join(', ')}
                                                            </span>
                                                            {isChildReg && (
                                                                <span className="text-[11px] text-surface-onVariant">
                                                                    {participants.length} {participants.length === 1 ? 'Kind' : 'Kinder'}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {isChildReg && (
                                                            isExpanded
                                                                ? <ChevronUp size={16} className="text-surface-onVariant ml-auto" />
                                                                : <ChevronDown size={16} className="text-surface-onVariant ml-auto" />
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-5 text-surface-onVariant text-sm">
                                                    {isChildReg ? registrant : <span className="opacity-50">—</span>}
                                                </td>
                                                <td className="px-6 py-5 text-surface-onVariant text-sm">
                                                    {email}
                                                </td>
                                                <td className="px-6 py-5">
                                                    {reg.status === 'pending' ? (
                                                        <span className="px-3 py-1 rounded-full text-[11px] font-black uppercase bg-tertiary-container text-tertiary-onContainer">Ausstehend</span>
                                                    ) : reg.status === 'confirmed' ? (
                                                        <span className="px-3 py-1 rounded-full text-[11px] font-black uppercase bg-primary-container text-primary-onContainer">Bestätigt</span>
                                                    ) : (
                                                        <span className="px-3 py-1 rounded-full text-[11px] font-black uppercase bg-error-container text-error-onContainer">Abgelehnt</span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-5 text-right">
                                                    {reg.status === 'pending' && (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleApproveRegistration(reg._id); }}
                                                            className="md3-btn-tonal h-9 px-4 text-xs font-bold gap-2"
                                                        >
                                                            <CheckCircle size={16} />
                                                            Freigeben
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                            {/* Expanded children detail rows */}
                                            {isChildReg && isExpanded && ad.children.filter(c => c.name?.trim()).map((child, idx) => (
                                                <tr key={`${reg._id}-child-${idx}`} className="bg-surface-containerLow/50">
                                                    <td className="pl-20 pr-6 py-3" colSpan={2}>
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-7 h-7 rounded-lg bg-secondary/10 text-secondary flex items-center justify-center text-xs font-black">
                                                                {idx + 1}
                                                            </div>
                                                            <div>
                                                                <span className="font-bold text-surface-on text-sm">{child.name}</span>
                                                                {child.age && <span className="text-surface-onVariant text-xs ml-2">({child.age} Jahre)</span>}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-3 text-surface-onVariant text-xs" colSpan={3}>
                                                        {child.allergies && (
                                                            <div className="flex items-center gap-1.5">
                                                                <Heart size={12} className="text-error" />
                                                                <span>{child.allergies}</span>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {filteredRegistrations.length === 0 && (
                        <div className="text-center py-16">
                            <Users size={48} className="mx-auto mb-4 opacity-20" />
                            <p className="text-surface-onVariant font-medium">
                                {searchTerm ? 'Keine Ergebnisse gefunden' : 'Noch keine Anmeldungen'}
                            </p>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default EventRegistrationsPage;
